import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@stockline/ui/components/button";
import { Slider } from "@stockline/ui/components/slider";

function formatTime(seconds: number): string {
  if (Number.isNaN(seconds) || !Number.isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

const SPEEDS = [1, 1.25, 1.5, 2];

/**
 * WhatsApp-style voice/audio bubble: a round play button, a seek bar, the
 * times and a speed toggle — no filename/size/type chrome and no oversized
 * native controls.
 */
export function ChatMediaAudio({ url }: { readonly url: string | null }) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  // MediaRecorder blobs (voice notes) report duration=Infinity from <audio>
  // metadata; when that happens we decode the bytes to get the real length.
  const [needsDecode, setNeedsDecode] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const readDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };
    const onLoaded = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        readDuration();
      } else {
        setNeedsDecode(true);
      }
    };
    const onTime = () => {
      setCurrentTime(audio.currentTime || 0);
      // Safari refines `duration` as it buffers — pick it up when it lands.
      readDuration();
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onError = () => {
      setFailed(true);
      setIsPlaying(false);
    };
    // Sync with externally-driven state changes — e.g. the browser pausing
    // this element because another <audio>/<video> started — so the play
    // button never freezes showing the wrong icon.
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("durationchange", readDuration);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("durationchange", readDuration);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.pause();
    };
  }, [url]);

  // iOS Safari can withhold loadedmetadata entirely under preload="metadata" —
  // if the duration is still unknown shortly after the URL arrived, decode.
  useEffect(() => {
    if (!url || duration > 0) return;
    const id = globalThis.setTimeout(() => setNeedsDecode(true), 1500);
    return () => globalThis.clearTimeout(id);
  }, [url, duration]);

  // Authoritative fallback: decode the audio bytes for the exact duration.
  // Only runs when metadata failed (voice notes); those files are small.
  useEffect(() => {
    if (!needsDecode || !url) return;
    let cancelled = false;
    const controller = new AbortController();
    const ctx = new AudioContext();
    fetch(url, { signal: controller.signal })
      .then((r) => r.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => {
        if (!cancelled && decoded.duration > 0) {
          setDuration((prev) => (prev > 0 ? prev : decoded.duration));
        }
      })
      .catch(() => {})
      .finally(() => void ctx.close().catch(() => {}));
    return () => {
      cancelled = true;
      // Abort the in-flight fetch too — `cancelled` alone only suppresses the
      // state write while the network request keeps running after unmount.
      controller.abort();
    };
  }, [needsDecode, url]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.playbackRate = playbackRate;
        await audio.play();
        setIsPlaying(true);
      }
    } catch {
      setFailed(true);
      setIsPlaying(false);
    }
  }, [isPlaying, playbackRate]);

  const handleSeek = (value: number[]) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const newTime = ((value[0] ?? 0) / 100) * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(playbackRate) + 1) % SPEEDS.length];
    setPlaybackRate(next);
    const audio = audioRef.current;
    if (audio) audio.playbackRate = next;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const loading = !url;

  return (
    <div
      className="flex w-[270px] max-w-full items-center gap-2.5 rounded-2xl border bg-card p-3"
      data-testid="chat-audio-player"
    >
      {url && (
        <audio ref={audioRef} src={url} preload="metadata" className="hidden">
          <track kind="captions" />
        </audio>
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={() => void togglePlay()}
        disabled={loading || failed}
        aria-label={
          isPlaying ? t("mobileChat.voicePause") : t("mobileChat.voicePlay")
        }
        className="size-10 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
      >
        {loading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : isPlaying ? (
          <Pause className="size-4" />
        ) : (
          <Play className="ml-0.5 size-4" />
        )}
      </Button>

      <div className="min-w-0 flex-1">
        {failed ? (
          <p className="text-xs text-destructive">
            {t("chat.failedToLoadAudio", "Échec du chargement de l'audio")}
          </p>
        ) : (
          <div className="space-y-1.5">
            <Slider
              value={[progress]}
              onValueChange={handleSeek}
              max={100}
              step={0.1}
              disabled={loading || duration === 0}
              aria-label={t("mobileChat.voiceSeek")}
              className="[&>span:first-child]:h-1.5 [&_[role=slider]]:size-3.5 [&_[role=slider]]:border-primary [&_[role=slider]]:bg-primary"
            />
            <div className="flex items-center justify-between text-[11px] font-medium text-muted-foreground">
              <span>{formatTime(currentTime)}</span>
              <span>{duration > 0 ? formatTime(duration) : "–:––"}</span>
            </div>
          </div>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={cycleSpeed}
        disabled={loading || failed}
        className="h-7 shrink-0 rounded-full px-2 text-[11px] font-semibold text-muted-foreground"
      >
        {playbackRate}x
      </Button>
    </div>
  );
}
